import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, Buildings, Truck } from "@phosphor-icons/react";
import PushToggle from "@/components/PushToggle";
import SearchableSelect from "@/components/SearchableSelect";

const EMPTY = { name: "", type: "physical", owner_user_id: "", owner_name: "", description: "" };

export default function Warehouses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "gestore";
  const [rows, setRows] = useState([]);
  const [operators, setOperators] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/warehouses");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadOperators = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/users/operators");
      setOperators(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); loadOperators(); /* eslint-disable-next-line */ }, []);

  // Operator SearchableSelect options. Each option carries the warehouse_id
  // the operator is currently linked to (if any) so we can show a warning
  // when reassigning them from another furgone.
  const operatorOpts = useMemo(() => operators.map((o) => {
    const currentWh = rows.find((w) => w.id === o.warehouse_id);
    return {
      value: o.id,
      label: o.name || o.email,
      sublabel: currentWh ? `Attualmente su: ${currentWh.name}` : "Nessun furgone assegnato",
      keywords: [o.name, o.email].filter(Boolean),
    };
  }), [operators, rows]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      type: r.type,
      owner_user_id: r.owner_user_id || "",
      owner_name: r.owner_name || "",
      description: r.description || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        type: form.type,
        owner_user_id: form.type === "mobile" ? (form.owner_user_id || null) : null,
        description: form.description,
      };
      if (editing) {
        await api.put(`/warehouses/${editing.id}`, payload);
        toast.success("Magazzino aggiornato");
      } else {
        await api.post("/warehouses", payload);
        toast.success("Magazzino creato");
      }
      setShowForm(false);
      await load();
      await loadOperators();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare "${r.name}"? Solo se vuoto.`)) return;
    try {
      await api.delete(`/warehouses/${r.id}`);
      toast.success("Magazzino eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const physical = rows.filter((r) => r.type === "physical");
  const mobile = rows.filter((r) => r.type === "mobile");

  const Card = ({ w }) => {
    const Icon = w.type === "physical" ? Buildings : Truck;
    return (
      <div className="bg-white border border-neutral-200 p-5 flex flex-col" data-testid={`warehouse-card-${w.id}`}>
        <div className="flex items-start justify-between mb-4">
          <div className={`w-10 h-10 flex items-center justify-center ${w.type === "physical" ? "bg-[#002FA7] text-white" : "bg-amber-500 text-white"}`}>
            <Icon size={20} weight="bold" />
          </div>
          <div className="flex items-center gap-1">
            {w.type === "mobile" && <PushToggle warehouseId={w.id} />}
            {isAdmin && (
              <>
                <button data-testid={`edit-warehouse-${w.id}`} onClick={() => openEdit(w)} className="p-1.5 hover:bg-neutral-100 transition-colors"><PencilSimple size={14} /></button>
                <button data-testid={`delete-warehouse-${w.id}`} onClick={() => remove(w)} className="p-1.5 hover:bg-red-50 text-red-600 transition-colors"><Trash size={14} /></button>
              </>
            )}
          </div>
        </div>
        <div className="label-eyebrow mb-1">{w.type === "physical" ? "Fisico" : "Mobile"}</div>
        <h3 className="font-display font-bold text-lg tracking-tight leading-tight mb-1">{w.name}</h3>
        {w.owner_name && <p className="text-xs text-neutral-500 mb-3">Tecnico: {w.owner_name}</p>}
        {w.description && <p className="text-xs text-neutral-600 mb-3">{w.description}</p>}
        <div className="mt-auto pt-3 border-t border-neutral-100 flex items-baseline justify-between">
          <span className="label-eyebrow">Giacenza</span>
          <span className="font-display font-black text-2xl tracking-tighter">{w.stock}</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title="Magazzini"
        subtitle="Fisici e mobili (furgoni tecnici). Traccia i pezzi in ogni location."
        action={
          isAdmin && (
            <button data-testid="new-warehouse-btn" onClick={openNew} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2">
              <Plus size={16} weight="bold" /> Nuovo Magazzino
            </button>
          )
        }
      />

      <div className="p-8 space-y-8">
        <section>
          <div className="flex items-center gap-3 mb-4">
            <Buildings size={20} className="text-[#002FA7]" weight="bold" />
            <h2 className="font-display font-bold text-xl tracking-tight">Magazzini Fisici</h2>
            <span className="label-eyebrow">{physical.length}</span>
          </div>
          {physical.length === 0 ? (
            <div className="bg-white border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">Nessun magazzino fisico. Aggiungine uno.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {physical.map((w) => <Card key={w.id} w={w} />)}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-3 mb-4">
            <Truck size={20} className="text-amber-600" weight="bold" />
            <h2 className="font-display font-bold text-xl tracking-tight">Magazzini Mobili · Furgoni Tecnici</h2>
            <span className="label-eyebrow">{mobile.length}</span>
          </div>
          {mobile.length === 0 ? (
            <div className="bg-white border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
              Nessun furgone/tecnico. Aggiungi un magazzino di tipo "Mobile" per ogni tecnico.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {mobile.map((w) => <Card key={w.id} w={w} />)}
            </div>
          )}
        </section>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[95vh] flex flex-col" data-testid="warehouse-form">
            <div className="p-4 md:p-6 border-b border-neutral-200 shrink-0">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuovo"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Magazzino</h2>
            </div>
            <div className="p-4 md:p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <span className="label-eyebrow block mb-2">Tipo *</span>
                <div className="grid grid-cols-2 gap-2">
                  {[["physical", "Fisico", Buildings], ["mobile", "Mobile (Furgone)", Truck]].map(([v, l, I]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`wh-type-${v}`}
                      onClick={() => setForm({ ...form, type: v })}
                      className={`flex items-center justify-center gap-2 py-3 border transition-colors text-sm font-medium ${form.type === v ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-neutral-300 bg-white hover:bg-neutral-50"}`}
                    >
                      <I size={16} weight="bold" /> {l}
                    </button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="label-eyebrow block mb-2">Nome *</span>
                <input required data-testid="warehouse-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.type === "mobile" ? "Es. Furgone Rossi Mario" : "Es. Magazzino Centrale"} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              {form.type === "mobile" && (
                <div>
                  <span className="label-eyebrow block mb-2">Tecnico assegnato</span>
                  <SearchableSelect
                    testid="warehouse-owner"
                    value={form.owner_user_id}
                    options={operatorOpts}
                    onChange={(v) => setForm({ ...form, owner_user_id: v })}
                    placeholder="Seleziona operatore…"
                  />
                  {operatorOpts.length === 0 ? (
                    <p className="mt-1 text-xs text-amber-700">
                      Nessun utente con ruolo <b>Operator</b> disponibile. Creane uno dalla pagina Utenti.
                    </p>
                  ) : form.owner_user_id && (() => {
                    const op = operators.find((o) => o.id === form.owner_user_id);
                    const currentWh = op && rows.find((w) => w.id === op.warehouse_id);
                    if (currentWh && currentWh.id !== (editing?.id || null)) {
                      return (
                        <p className="mt-1 text-xs text-amber-700">
                          ⚠️ {op.name || op.email} è attualmente su <b>{currentWh.name}</b>. Salvando verrà riassegnato a questo furgone.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              <label className="block">
                <span className="label-eyebrow block mb-2">Descrizione</span>
                <textarea data-testid="warehouse-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
            </div>
            <div className="p-4 md:p-6 pt-3 flex justify-end gap-2 border-t border-neutral-200 shrink-0">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="warehouse-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
